import React, { createContext, PropsWithChildren, useContext } from 'react';
import OriginTracing from '@atlassiansox/origin-tracing';
import { Product } from '@atlassiansox/bxpkit-core';
import { InjectedIntl } from 'react-intl';
import { useSignUpFormConfig } from '../../../hook';
import {
  FeatureFlags,
  LocaleType,
  ProductDataType,
  UserDataType
} from '../../../model';

import {
  BundleData,
  FREE_EDITION_LABEL_KEY,
  getBundleData,
  getFeaturesList,
  getPageHeaderLabels,
  PageHeaderLabels
} from '../../../utility';
import { SimpleSignupImageUrlsType } from '../types';
import { accountOperationUrl } from '../utility';

export type SimpleSignupContextProps = {
  readonly labels: {
    [key: string]: string;
  };
  readonly productData: { [key: string]: ProductDataType };
  readonly intl: InjectedIntl;
  readonly enableSkipRecaptcha?: boolean;
  readonly locale: LocaleType;
  readonly simpleSignupImageUrls: SimpleSignupImageUrlsType;
  readonly userInfo?: UserDataType;
  readonly featureFlags: FeatureFlags;
};

export type SimpleSignupState = {
  readonly bundleData: BundleData;
  readonly pageHeaderLabels: PageHeaderLabels;
  readonly featuresList: string;
  readonly userInfo?: UserDataType;
  readonly signInAccountUrl: string;
  readonly switchAccountUrl: string;
  readonly switchAccountOrigin: OriginTracing;
  readonly productKeys: Product[];
  readonly intl: InjectedIntl;
  readonly enableSkipRecaptcha: boolean;
  readonly locale: LocaleType;
  readonly simpleSignupImageUrls: SimpleSignupImageUrlsType;
  readonly featureFlags: FeatureFlags;
};

export const SimpleSignupContext = createContext<SimpleSignupContextProps>({
  // @ts-ignore
  intl: {},
  labels: {},
  productData: {},
  simpleSignupImageUrls: {
    atlassianLogoImage: '',
    jswMobileNavImage: ''
  },
  featureFlags: {}
});

export function SimpleSignupProvider({
  children,
  ...props
}: PropsWithChildren<SimpleSignupContextProps>) {
  return (
    <SimpleSignupContext.Provider value={props}>
      {children}
    </SimpleSignupContext.Provider>
  );
}

export function useSimpleSignupState(): SimpleSignupState {
  const {
    labels,
    locale,
    productData,
    userInfo,
    simpleSignupImageUrls,
    intl,
    featureFlags,
    enableSkipRecaptcha = false
  } = useContext(SimpleSignupContext);

  const { bundle: bundleName, product, edition } = useSignUpFormConfig();

  const bundleData = getBundleData({
    activeBundlesMap: productData,
    bundleName,
    productKeys: product,
    edition
  });
  const pageHeaderLabels = getPageHeaderLabels({
    labels,
    bundleData
  });
  const featuresList = getFeaturesList({
    labels,
    bundleData,
    featuresListEditionLabelKey: FREE_EDITION_LABEL_KEY.toLowerCase()
  });
  const { switchAccountUrl, switchAccountOrigin, signInAccountUrl } =
    accountOperationUrl();

  const productKeys = bundleData.products.map((product) => {
    return {
      edition: bundleData.edition,
      imgSource: product.productLogoUrl,
      product: product.productKey,
      serverDownloadContent: null
    };
  });

  return {
    intl,
    pageHeaderLabels,
    bundleData,
    featuresList,
    userInfo,
    signInAccountUrl,
    switchAccountUrl,
    switchAccountOrigin,
    productKeys,
    enableSkipRecaptcha,
    locale,
    simpleSignupImageUrls,
    featureFlags
  };
}
